import type { DefaultNamespace } from 'i18next'
import {
	type CSSProperties,
	forwardRef,
	type HTMLInputTypeAttribute,
	type InputHTMLAttributes,
	type ReactNode,
	useState
} from 'react'
import { EyeIcon, EyeOffIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { InputStatusIcon } from '@/components/ui/input-status-icon'
import { InputWrapper } from '@/components/ui/input-wrapper'
import { Label } from '@/components/ui/label'
import { cn } from '@/helpers/utils'

export interface InputTextProps extends InputHTMLAttributes<HTMLInputElement> {
	className?: string
	label?: DefaultNamespace | ReactNode
	name?: string
	type: HTMLInputTypeAttribute
	typeIcon?: ReactNode
	touched?: boolean
	errorMessage?: string | DefaultNamespace
	visibilityToggle?: boolean
	helpText?: string | DefaultNamespace
	placeholder?: string
	inputSize?: 'sm' | 'md' | 'lg'
	width?: CSSProperties['width']
}

const inputSizeStyles = {
	sm: 'h-10 px-3 pr-9',
	md: 'h-11 px-3.5 pr-9',
	lg: 'h-12 px-4 pr-9'
}

const visibleToggleSizeStyles = {
	sm: 'h-10 py-2 px-3',
	md: 'h-11 py-2.5 px-3',
	lg: 'h-12 py-2 px-3'
}

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
	(
		{
			label,
			name,
			type,
			typeIcon,
			touched = false,
			errorMessage,
			helpText,
			className,
			disabled,
			placeholder,
			visibilityToggle = false,
			inputSize = 'md',
			width = '100%',
			...rest
		},
		ref
	) => {
		const [eyeVisible, setEyeVisible] = useState(false)

		return (
			<div
				className={cn('flex flex-col gap-1.5', className)}
				style={{ width }}
			>
				{label && <Label htmlFor={name}>{label}</Label>}
				<div className='flex w-full items-center'>
					<InputWrapper
						className={inputSizeStyles[inputSize]}
						hasError={!!errorMessage}
						touched={touched}
						visibilityToggle={visibilityToggle}
					>
						{typeIcon}
						<input
							className='h-full w-full flex-1 border-none text-base text-neutral-900 outline-none placeholder:text-neutral-400 focus:outline-none disabled:cursor-not-allowed'
							disabled={disabled}
							name={name}
							placeholder={placeholder}
							ref={ref}
							type={eyeVisible ? 'text' : type}
							{...rest}
						/>
						<InputStatusIcon
							hasError={!!errorMessage}
							touched={touched}
						/>
					</InputWrapper>

					{visibilityToggle && (
						<button
							className={cn(
								'rounded-r-lg border border-neutral-300 border-l-0 text-neutral-700',
								disabled
									? 'cursor-not-allowed'
									: 'cursor-pointer',
								visibleToggleSizeStyles[inputSize]
							)}
							onClick={() =>
								!disabled && setEyeVisible(!eyeVisible)
							}
							type='button'
						>
							{eyeVisible ? (
								<EyeIcon data-color size={20} />
							) : (
								<EyeOffIcon data-color size={20} />
							)}
						</button>
					)}
				</div>
				<InputHelpText error={errorMessage} helpText={helpText} />
			</div>
		)
	}
)

InputText.displayName = 'InputText'
